import { mapContactDetailToFormInitialValues } from "design-components";
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";

const selectCustomerDetailState = (state: RootState) => state.customerDetail;

export const selectCustomer = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.customer,
);

export const selectCustomerUsers = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.users,
);

export const selectCustomerLocations = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.locations,
);

export const selectCustomerLocationsLoading = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.locationsLoading,
);

export const selectCustomerLocationsError = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.locationsError,
);

export const selectCustomerLocationsSearch = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.locationsSearch,
);

export const selectCustomerLocationsCanSearch = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.locationsCanSearch,
);

export const selectCustomerDetailLoading = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.loading,
);

export const selectCustomerDetailError = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.error,
);

export const selectSelectedCustomerId = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.selectedCustomerId,
);

export const selectCustomerName = createSelector(
  [selectCustomer],
  (customer) => customer?.name || "",
);

export const selectCustomerStatus = createSelector(
  [selectCustomer],
  (customer) => customer?.status || "Unknown",
);

export const selectCustomerAvatar = createSelector(
  [selectCustomer],
  (customer) => customer?.logoUrl || "",
);

export const selectCustomerInfo = createSelector(
  [selectCustomer],
  (customer) => {
    if (!customer) return null;
    return {
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      status: customer.status,
      logo: customer.logoUrl,
    };
  },
);

export const selectHasCustomerData = createSelector(
  [selectCustomer],
  (customer) => customer !== null,
);

export const selectCustomerIsActive = createSelector([selectCustomer], (customer) => {
  const status = String(customer?.status ?? "").trim().toLowerCase();
  return status === "active";
});

export const selectCustomerStatusSubmitting = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.statusSubmitting,
);

export const selectCustomerUserInviteSubmitting = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.userInviteSubmitting,
);

export const selectCustomerLogoUploading = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.logoUploading,
);

export const selectCustomerContactsListQuery = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactsListQuery,
);

export const selectCustomerContactsPageRows = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contacts,
);

export const selectCustomerContactsTotal = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactsTotalCount,
);

export const selectCustomerContactRoles = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactRoles ?? [],
);

export const selectCustomerContactsLoading = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactsLoading,
);

export const selectCustomerContactsError = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactsError,
);

export const selectCustomerContactsSearch = createSelector(
  [selectCustomerContactsListQuery],
  (query) => query.search,
);

export const selectCustomerContactsRoleCodes = createSelector(
  [selectCustomerContactsListQuery],
  (query) => query.roleCodes,
);

export const selectCustomerContactsSortDescending = createSelector(
  [selectCustomerContactsListQuery],
  (query) => query.sortDescending,
);

export const selectCustomerContactsFromIndex = createSelector(
  [selectCustomerContactsListQuery],
  (query) => query.fromIndex,
);

export const selectCustomerContactFormSubmitting = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactFormSubmitting,
);

export const selectCustomerContactEditLoading = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactEditLoading,
);

const selectCustomerContactEditDetail = createSelector(
  [selectCustomerDetailState],
  (customerDetail) => customerDetail.contactEditDetail,
);

export const selectCustomerContactFormInitialValues = createSelector(
  [selectCustomerContactEditDetail],
  (detail) => {
    if (!detail) return null;
    return mapContactDetailToFormInitialValues(detail);
  },
);
